import { z } from 'zod';
/**
 * Base environment variables shared across all environments
 */
const envSchemaBase = {
    DEFAULT_STREAM_DOMAIN: z
        .string()
        .default('tv.streamr.eth')
        .transform((v) => v),
    SPONSORSHIP_BACKEND_URL: z.url().optional(),
    PURCHASE_PUBLISHABLE_KEY: z.string(),
    PURCHASE_BACKEND_URL: z.url(),
    RECORDER_START_URL: z.url(),
    RECORDER_STOP_URL: z.url(),
    RECORDER_STORAGE_URL: z.url(),
    THUMBNAIL_BACKEND_URL: z.url(),
    USER_FILES_STORAGE_URL: z.url(),
    RTMP_PROXY_URL: z
        .string()
        .default('wss://fe6a54d8-8d6f-4743-890d-e9ecd680a4c7.xyz/rtmp'),
    DEV_MOCK_PLUGINS_COUNT: z.coerce.number().default(0),
    DEV_RECORDER_GATEWAY_URL: z.url().optional(),
    SENTRY_DSN: z
        .string()
        .default('https://ebbd2fe34d42f187169389a28887ca80@o151964.ingest.us.sentry.io/4509082246971392'),
    BUILD_TRANSFORMS_FILENAMES: z
        .string()
        .default('false')
        .transform((v) => v === 'true'),
    ENABLE_MATOMO_IN_DEV: z
        .string()
        .default('false')
        .transform((v) => v === 'true'),
    ENABLED_FEATURES: z
        .string()
        .default('')
        .transform((v) => {
        const flags = new Set();
        v.split(',').forEach((flag) => {
            const trimmedFlag = flag.trim();
            if (trimmedFlag) {
                flags.add(trimmedFlag);
            }
        });
        return flags;
    }),
};
/**
 * Development-specific environment variables.
 * Includes gateway ports and local service URLs for development.
 */
const developmentEnvSchema = {
    ...envSchemaBase,
    NODE_ENV: z.literal('development'),
    DEV_CHAT_SECURE_GATEWAY_PORT: z.coerce.number(),
    DEV_CHAT_INSECURE_GATEWAY_PORT: z.coerce.number(),
    DEV_PURCHASE_BACKEND_SECURE_GATEWAY_PORT: z.coerce.number(),
    DEV_PURCHASE_BACKEND_INSECURE_GATEWAY_PORT: z.coerce.number(),
    DEV_MOCK_PLUGIN_SECURE_GATEWAY: z.string(),
    DEV_MOCK_PLUGIN_INSECURE_GATEWAY: z.string(),
};
/**
 * Production-specific environment variables.
 * Minimal configuration required for production deployments.
 */
const productionEnvSchema = {
    ...envSchemaBase,
    NODE_ENV: z.literal('production'),
};
/**
 * Test-specific environment variables.
 * Extends development schema for testing with mock services.
 */
const testEnvSchema = {
    ...developmentEnvSchema,
    NODE_ENV: z.literal('test'),
};
/**
 * Final environment schema discriminated by NODE_ENV.
 * Routes to appropriate schema based on NODE_ENV value.
 */
const envSchema = z.discriminatedUnion('NODE_ENV', [
    z.object(developmentEnvSchema),
    z.object(productionEnvSchema),
    z.object(testEnvSchema),
]);
/**
 * Parse and validate environment variables against the appropriate schema.
 * Uses `import.meta.env` for Vite (ESM), falls back to `process.env` for Jest/Node.js (CommonJS).
 * Throws on validation failure with error details.
 */
const parseEnv = () => {
    try {
        const vars = envSchema.parse(import.meta.env ??
            (typeof process !== 'undefined' ? process.env : {}));
        return {
            ...vars,
            isProduction() {
                return this.NODE_ENV === 'production';
            },
            isFeatureEnabled(featureKey) {
                return this.ENABLED_FEATURES.has(featureKey);
            },
        };
    }
    catch (err) {
        console.error('Invalid environment variables', err);
        throw err;
    }
};
/**
 * Validated environment variables object with type guards. Automatically discriminated by NODE_ENV
 * value at runtime. Contains fully typed, environment-specific config with defaults applied.
 * Throws during module load if validation fails – fail fast approach.
 *
 * Type guard methods:
 * - `env.isProduction()` – narrows type when NODE_ENV is 'production'
 *
 * @example
 * ```ts
 * import { env } from './env'
 *
 * const url = env.PURCHASE_BACKEND_URL
 *
 * if (env.isProduction()) {
 *   // Type narrowed: NODE_ENV is 'production'
 * }
 * ```
 */
export const Env = parseEnv();
